import { CardKind, Commands, Fix64, ScriptedPlayer, SimConstants } from "@highmarch/domain";
import type { Cell, Command, CommandResult } from "@highmarch/domain";
import type { LevelParts } from "./levelParts";

/** How many decisions the full-road draft makes at most before it gives up on the level. */
const DraftMaxSteps = 6000;
/** How many one-second advances with no card on the table the draft plays before calling the road grown enough. */
const DraftMaxAdvances = 200;

/** The headless tools' way into a level (LevelController's debug* methods): commands, time and staging, no UI. */
export class LevelDebug {
  /** A headless capture's command is running: it aims the camera itself, so no placement glide may drift it away. */
  driving = false;

  constructor(private readonly p: LevelParts) {}

  /** M36 (captures): the next stage to start is a rare stage (the sim's own `debugForceRareNextStage`). */
  rare(): void { this.p.sim.debugForceRareNextStage(); }

  /** M40 (captures): the next stage to start is the Starfall Wyrm's (the sim's own `debugForceWyrmNextStage`). */
  wyrm(): void { this.p.sim.debugForceWyrmNextStage(); }

  /**
   * M40 (captures): the living Wyrm's HP set to `frac` of its full HP — low for the kill path, huge (1000) for the escape
   * path (its bar reads full; the board cannot bring it down). False when no Wyrm is on the road. A dev write, never logged.
   */
  wyrmHp(frac: number): boolean {
    const sim = this.p.sim, w = sim.wyrm, e = w ? sim.findEnemy(w.enemyId) : null;
    if (!e || !(frac > 0)) return false;
    e.hp = Fix64.max(Fix64.One, e.maxHp.mul(Fix64.parse(frac.toFixed(4))));
    return true;
  }

  /** M40 (captures): the Wyrm's rally deals no offers (an open rally offer is skipped, as the SKIP button would). */
  wyrmNoRally(): void {
    const sim = this.p.sim;
    sim.rareOffersLeft = 0;
    if (sim.currentOffer?.rare === true) this.execute(Commands.skipOffer());
  }

  /** Issues a command directly (bypasses the UI). */
  execute(command: Command): CommandResult {
    const r = this.p.session.execute(command);
    this.driving = true;
    try { this.p.afterSimChange(false); } finally { this.driving = false; }
    return r;
  }

  /** Lets the scripted player make its next decision (or step one tick). Returns whether it acted. */
  autoplay(player: ScriptedPlayer): boolean {
    const c = player.next(this.p.sim);
    if (c) { this.execute(c); return true; }
    return false;
  }

  /** Advances the simulation by wall-clock seconds without waiting for animation frames. */
  advance(seconds: number, render: boolean): void {
    const p = this.p, ticks = Math.round(seconds * SimConstants.TicksPerSecond);
    for (let i = 0; i < ticks && !p.sim.isOver; i++) { p.session.tick(); p.ticks.afterDebugTick(); }
    p.afterSimChange(false);
    p.scene.flush();
    p.scene.enemies.interpolate(1, 0);
    // the frame loop puts the end screen up after a rare's escape (its ribbon holds first, LevelEnd.due)
    if (p.sim.isOver && !p.end.ended && p.sim.rareRun?.outcome !== "escaped") p.end.show();
    if (render) p.rig.render();
  }

  /**
   * Drafts road until it reaches the level's road pace knee (`roadPaceTilesAt`: the length up to which every in-run
   * offer carries road, DESIGN §4.4), for the headless level validator. The road never finishes, so the knee is the
   * length a level must have room for early on; it keeps growing past it more slowly.
   *
   * This takes every path card it is offered and places it where it reaches furthest, and reports how far it got.
   */
  draftFullRoad(): { drafted: number; target: number; stalled: boolean; over: boolean } {
    const player = new ScriptedPlayer();
    const target = this.p.sim.balance.tables.roadPaceTilesAt(this.p.sim.levelNumber);
    let advances = 0;
    for (let i = 0; i < DraftMaxSteps; i++) {
      const sim = this.p.sim;
      if (sim.road.designedTileCount >= target || sim.isOver) break;
      const offer = sim.currentOffer;
      // Road comes over the whole level, not up front: when there is no offer on the table the level has to be played
      // on — waves started, stages called, time advanced — before the next card comes round.
      if (offer === null) {
        const c = player.next(sim);
        if (c) { this.execute(c); continue; }
        if (++advances > DraftMaxAdvances) break;              // far enough into the level to know the road can grow
        this.advance(1, false);
        continue;
      }
      if (!offer.hasPick) {
        const path = offer.cards.findIndex((c) => c.kind === CardKind.Path);
        this.execute(path >= 0 ? Commands.pickCard(path) : Commands.skipOffer());
        continue;
      }
      if (offer.pickedCard.kind !== CardKind.Path) { this.execute(Commands.skipOffer()); continue; }
      if (offer.ghosts.length === 0) return { drafted: sim.road.designedTileCount, target, stalled: true, over: sim.isOver };
      // the ghost whose far end lands furthest from the base, so the road really uses the map
      let best = 0, far = -1;
      for (let g = 0; g < offer.ghosts.length; g++) {
        const cells = offer.ghosts[g].cells, e = cells[cells.length - 1];
        const d = Math.abs(e.x - sim.grid.base.x) + Math.abs(e.y - sim.grid.base.y);
        if (d > far) { far = d; best = g; }
      }
      this.execute(Commands.placeSegment(best));
    }
    const sim = this.p.sim;
    return { drafted: sim.road.designedTileCount, target, stalled: false, over: sim.isOver };
  }

  /**
   * Dev only: stage one tower beside the road with nothing else built, for the capture that shows each tower in
   * turn. Returns the cell it went on, or null if there was nowhere legal.
   */
  stageTower(archetype: number, tier: number, along: number, near: { x: number; y: number } | null): Cell | null {
    const sim = this.p.sim;
    sim.debugClearTowers();
    const road = sim.road.roadCells;
    if (road.length < 3) return null;
    const want = near ?? road[Math.max(1, Math.min(road.length - 2, Math.round(road.length * along)))];
    // nearest legal build cell to the point we want the camera on
    const cells = sim.collectValidTowerCells();
    if (cells.length === 0) return null;
    let best = cells[0], bestD = Infinity;
    for (const c of cells) {
      const d = Math.hypot(c.x - want.x, c.y - want.y);
      if (d < bestD) { bestD = d; best = c; }
    }
    const t = sim.debugAddTower(archetype, best, tier);
    if (!t) return null;
    this.p.afterSimChange(true);
    this.p.scene.flush();
    return best;
  }
}
